import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Sparkles, Award, HeartPulse, ChevronDown } from "lucide-react";
import { BOOKING_URL, IMAGES } from "../lib/data";

const HERO_PHOTOS = [
  { src: IMAGES.hero, alt: "Cavoodle being gently scissored on the face" },
  { src: IMAGES.gallery[1], alt: "Cavalier being trimmed" },
  { src: IMAGES.gallery[4], alt: "Golden retriever being brushed" },
  { src: IMAGES.gallery[2], alt: "Chocolate Labrador puppy nail trim" },
  { src: IMAGES.gallery[3], alt: "Paw pad trim close up" },
];

export default function Hero() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % HERO_PHOTOS.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24"
    >
      <div className="lilac-blur hero-anim-blur" style={{ top: "-200px", right: "-150px" }} />
      <div className="brown-blur hero-anim-blur" style={{ bottom: "-220px", left: "-180px", animationDelay: "1.5s" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left copy */}
          <div className="lg:col-span-7">
            <div
              className="hero-stagger inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lilac-soft border border-[#E6DEC3] mb-8"
              style={{ animationDelay: "0.05s" }}
              data-testid="hero-tagline-chip"
            >
              <Sparkles size={14} className="text-lilac-dark hero-sparkle" />
              <span className="eyebrow !text-[0.65rem] !text-brown-dark">
                Boutique Grooming Salon · Est. 2025
              </span>
            </div>

            <h1
              className="hero-stagger font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[7.5rem] leading-[0.9] text-brown-dark tracking-tight"
              style={{ animationDelay: "0.15s" }}
            >
              A calmer kind
              <br />
              of <em className="italic font-medium text-lilac-dark hero-word-underline">grooming</em>
              <br />
              day.
            </h1>

            <p
              className="hero-stagger mt-8 max-w-xl text-lg text-text-secondary leading-relaxed"
              style={{ animationDelay: "0.35s" }}
            >
              Barks &amp; Co is a private one-on-one dog grooming service.
              No cages, no queues — just your pup, your groomer, and a
              beautifully gentle spa experience from start to finish.
            </p>

            {/* Credential chips */}
            <div
              className="hero-stagger mt-6 flex flex-wrap gap-2.5"
              style={{ animationDelay: "0.5s" }}
              data-testid="hero-credentials"
            >
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cream border border-[#E6DEC3] text-xs text-brown-dark">
                <Award size={13} className="text-lilac-dark" />
                Certified &amp; Qualified Groomer
              </div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cream border border-[#E6DEC3] text-xs text-brown-dark">
                <HeartPulse size={13} className="text-lilac-dark" />
                Pet First Aid Trained
              </div>
            </div>

            <div
              className="hero-stagger mt-8 flex flex-wrap items-center gap-4"
              style={{ animationDelay: "0.65s" }}
            >
              <a
                href={BOOKING_URL}
                data-testid="hero-book-button"
                className="btn-primary group"
              >
                Join The Waitlist
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <a
                href="#services"
                data-testid="hero-services-button"
                className="btn-outline"
              >
                View Services
              </a>
            </div>

            <div
              className="hero-stagger mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-brown-warm"
              style={{ animationDelay: "0.8s" }}
            >
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-lilac-dark" />
                <span>By appointment · Auckland, NZ</span>
              </div>
              <div className="h-1 w-1 rounded-full bg-brown-warm/40 hidden sm:block" />
              <div>1-on-1, cage-free grooming</div>
            </div>
          </div>

          {/* Right: rotating photo carousel */}
          <div
            className="lg:col-span-5 relative hero-stagger hero-float"
            style={{ animationDelay: "0.25s" }}
          >
            <div className="absolute -inset-8 bg-lilac-soft rounded-[3rem] -rotate-3 -z-10" />
            <div className="arch-top shadow-2xl shadow-brown-dark/10 relative w-full h-[520px] lg:h-[600px]">
              {HERO_PHOTOS.map((p, idx) => (
                <img
                  key={p.src}
                  src={p.src}
                  alt={p.alt}
                  data-testid={`hero-photo-${idx}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-in-out ${
                    idx === i ? "opacity-100 hero-kenburns" : "opacity-0"
                  }`}
                />
              ))}
              {/* Dots */}
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {HERO_PHOTOS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setI(idx)}
                    data-testid={`hero-dot-${idx}`}
                    aria-label={`View photo ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === i ? "w-6 bg-cream" : "w-1.5 bg-cream/50 hover:bg-cream/80"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* floating quote badge */}
            <div
              className="absolute -left-4 lg:-left-10 bottom-8 bg-cream rounded-2xl p-5 shadow-xl border border-[#E6DEC3] max-w-[230px] hero-badge-pop"
              data-testid="hero-badge"
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className="h-1.5 w-1.5 rounded-full bg-lilac-dark hero-dot-pulse" />
                <div className="eyebrow !mb-0">Now Booking</div>
              </div>
              <p className="font-serif italic text-brown-dark text-lg leading-snug">
                "Every pup leaves a little fluffier — and a lot happier."
              </p>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="hidden md:flex justify-center mt-16">
          <a
            href="#about"
            data-testid="hero-scroll-cue"
            className="hero-scroll-cue inline-flex flex-col items-center gap-1 text-brown-warm hover:text-lilac-dark transition-colors"
            aria-label="Scroll down"
          >
            <span className="eyebrow !text-[0.55rem]">Discover the studio</span>
            <ChevronDown size={18} />
          </a>
        </div>
      </div>

      {/* Marquee divider */}
      <div className="mt-14 overflow-hidden border-y border-[#E6DEC3] bg-cream-alt py-5">
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0">
              {[
                "Boutique Grooming",
                "Cage-Free Care",
                "By Appointment Only",
                "1-on-1 Studio",
                "Calm · Gentle · Patient",
                "Est. 2025",
              ].map((t, idx) => (
                <div key={idx} className="flex items-center px-8">
                  <span className="font-serif italic text-2xl md:text-3xl text-brown-warm/70">
                    {t}
                  </span>
                  <span className="mx-8 text-lilac-dark text-xl">✦</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
